import { Icon20Cancel } from '@vkontakte/icons';
import { Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { FC, memo, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { Button } from 'reactApp/ui/Button/Button';

import { MobileDialog } from '../Mobile/MobileDialog/MobileDialog';
import styles from './AsidePromoModal.css';
import { IAsidePromoModalProps } from './AsidePromoModal.types';

interface ContainerProps {
    isMobile: boolean;
    onClose: () => void;
}

const Container: FC<PropsWithChildren<ContainerProps>> = ({ isMobile, children, onClose }) => {
    if (isMobile) {
        return (
            <MobileDialog
                id="aside-promo-modal"
                onClose={onClose}
                mod="base"
                topmost
                closeOnDimmerClick
                className={styles['mobile-dialog']}
            >
                {children}
            </MobileDialog>
        );
    }

    return <>{children}</>;
};

export const AsidePromoModal = memo<IAsidePromoModalProps>(
    ({ theme, title, text, imageUrl, buttonText, href, onShow, onClick, onClose, fixed = true }) => {
        const [show, setShow] = useState(true);

        useEffect(() => {
            onShow?.();
        }, []);

        const handleClick = useCallback(() => {
            setShow(false);
            onClick?.();
        }, [onClick, setShow]);

        const handleClose = useCallback(() => {
            setShow(false);
            onClose?.();
        }, [onClose, setShow]);

        if (!show) {
            return null;
        }

        const isMobile = EnvironmentSelectors.isMobile();

        return (
            <Container isMobile={isMobile} onClose={handleClose}>
                <div
                    className={classNames({
                        [styles.root]: !isMobile,
                        [styles.root_fixed]: !isMobile && fixed,
                        [styles.root_mobile]: isMobile,
                        [styles[`root_${theme}`]]: Boolean(theme),
                    })}
                >
                    <div className={styles.imageWrapper}>
                        <img className={styles.image} src={imageUrl} alt="Cover image" />
                    </div>
                    <div
                        className={classNames(styles.content, {
                            [styles.content_mobile]: isMobile,
                        })}
                    >
                        <Text className={styles.title}>{title}</Text>
                        <Spacing size={isMobile ? 12 : 8} />
                        <Text
                            className={classNames(styles.text, {
                                [styles.text_mobile]: isMobile,
                            })}
                        >
                            {text}
                        </Text>
                        <Spacing size={isMobile ? 40 : 20} />
                        <Button
                            className={classNames(styles.button, {
                                [styles.button_mobile]: isMobile,
                            })}
                            href={href}
                            theme="vk"
                            primary
                            target="_blank"
                            onClick={handleClick}
                        >
                            {buttonText}
                        </Button>
                    </div>
                    {!isMobile && (
                        <div className={styles.close} onClick={handleClose}>
                            <Icon20Cancel width={18} height={18} />
                        </div>
                    )}
                </div>
            </Container>
        );
    }
);

AsidePromoModal.displayName = 'AsidePromoModal';
